import { useState, useEffect, useCallback, useRef, type DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';

const ADMIN_KEY = 'admin_key';

interface QuestionRow {
  id: number;
  questionText: string;
  difficulty: number;
  tag: string;
  status: string;
}

interface QuestionFormData {
  questionText: string;
  options: [string, string, string, string];
  correctIndex: number;
  difficulty: number;
  tag: string;
}

const EMPTY_FORM: QuestionFormData = {
  questionText: '',
  options: ['', '', '', ''],
  correctIndex: 0,
  difficulty: 1,
  tag: '',
};

const DIFF_LABEL: Record<number, string> = { 1: '简单', 2: '中等', 3: '困难' };
const DIFF_OPTIONS = [
  { value: 1, label: '简单' },
  { value: 2, label: '中等' },
  { value: 3, label: '困难' },
];

export default function AdminScreen() {
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);

  // Auth
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem(ADMIN_KEY) || '');
  const [authenticated, setAuthenticated] = useState(false);

  // Questions
  const [questions, setQuestions] = useState<QuestionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Import
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<{ count: number; first: string } | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState('');
  const [dragOver, setDragOver] = useState(false);

  // Delete confirm
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);

  // Create / Edit form
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<QuestionFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const headers = useCallback(() => ({ 'Content-Type': 'application/json', 'x-admin-key': adminKey }), [adminKey]);

  // Verify admin key on mount
  useEffect(() => {
    if (!adminKey) return;
    fetch('/api/v1/admin/questions', { headers: headers() })
      .then((r) => {
        if (r.ok) {
          setAuthenticated(true);
          return r.json();
        }
        throw new Error('auth failed');
      })
      .then((d) => setQuestions(d.questions || []))
      .catch(() => {
        setAuthenticated(false);
        sessionStorage.removeItem(ADMIN_KEY);
      });
  }, [adminKey, headers]);

  const handleLogin = async () => {
    try {
      const res = await fetch('/api/v1/admin/questions', {
        headers: { 'x-admin-key': adminKey },
      });
      if (res.ok) {
        sessionStorage.setItem(ADMIN_KEY, adminKey);
        setAuthenticated(true);
        const d = await res.json();
        setQuestions(d.questions || []);
      } else {
        setError('密钥错误');
      }
    } catch {
      setError('连接失败');
    }
  };

  const fetchQuestions = useCallback(() => {
    fetch('/api/v1/admin/questions', { headers: headers() })
      .then((r) => r.json())
      .then((d) => setQuestions(d.questions || []))
      .catch(() => setError('加载题目列表失败'));
  }, [headers]);

  // File handling
  const handleFile = useCallback((f: File) => {
    setFile(f);
    setImportResult('');
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result as string);
        const questions = Array.isArray(data) ? data : data.questions;
        if (!questions?.length) {
          setPreview(null);
          setError('JSON 中未找到题目数组');
          return;
        }
        setError('');
        setPreview({ count: questions.length, first: questions[0]?.questionText || '' });
      } catch {
        setPreview(null);
        setError('JSON 格式无效');
      }
    };
    reader.readAsText(f);
  }, []);

  const onDrop = useCallback((e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f && f.name.endsWith('.json')) handleFile(f);
  }, [handleFile]);

  const handleImport = async () => {
    if (!file || !preview) return;
    setImporting(true);
    setImportResult('');
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const questions = Array.isArray(data) ? data : data.questions;
      const res = await fetch('/api/v1/admin/import', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ questions }),
      });
      if (!res.ok) throw new Error((await res.json()).message || 'import failed');
      const result = await res.json();
      setImportResult(`导入完成: 新增 ${result.inserted} 条, 跳过 ${result.skipped} 条`);
      setFile(null);
      setPreview(null);
      if (fileRef.current) fileRef.current.value = '';
      fetchQuestions();
    } catch (e: any) {
      setImportResult(`导入失败: ${e.message}`);
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm(`确认删除题目 #${id}?`)) return;
    try {
      const res = await fetch(`/api/v1/admin/questions/${id}`, {
        method: 'DELETE',
        headers: headers(),
      });
      if (!res.ok) throw new Error('delete failed');
      fetchQuestions();
    } catch (e: any) {
      setError(`删除失败: ${e.message}`);
    }
  };

  const handleDeleteAll = async () => {
    try {
      const res = await fetch('/api/v1/admin/questions/delete-all', {
        method: 'POST',
        headers: headers(),
      });
      if (!res.ok) throw new Error('delete failed');
      setConfirmDeleteAll(false);
      setQuestions([]);
    } catch (e: any) {
      setError(`删除全部失败: ${e.message}`);
    }
  };

  const openCreateForm = () => {
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setFormError('');
    setShowForm(true);
  };

  const openEditForm = async (id: number) => {
    try {
      const res = await fetch(`/api/v1/admin/questions/${id}`, { headers: headers() });
      if (!res.ok) throw new Error('not found');
      const d = await res.json();
      const q = d.question;
      setEditingId(id);
      setFormData({
        questionText: q.questionText,
        options: q.options,
        correctIndex: q.correctIndex,
        difficulty: q.difficulty,
        tag: q.tag,
      });
      setFormError('');
      setShowForm(true);
    } catch {
      setError('加载题目详情失败');
    }
  };

  const handleSave = async () => {
    const { questionText, options, correctIndex, difficulty, tag } = formData;
    if (!questionText.trim() || options.some((o) => !o.trim()) || !tag.trim()) {
      setFormError('所有字段都必须填写');
      return;
    }

    setSaving(true);
    setFormError('');
    try {
      const url = editingId
        ? `/api/v1/admin/questions/${editingId}`
        : '/api/v1/admin/questions';
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: headers(),
        body: JSON.stringify({
          questionText: questionText.trim(),
          options: options.map((o) => o.trim()) as [string, string, string, string],
          correctIndex,
          difficulty,
          tag: tag.trim(),
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: '保存失败' }));
        throw new Error(err.message);
      }

      setShowForm(false);
      fetchQuestions();
    } catch (e: any) {
      setFormError(e.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  // Login screen
  if (!authenticated) {
    return (
      <div style={{ padding: 'var(--space-lg) var(--space-md)', maxWidth: 360, margin: '40px auto' }}>
        <div className="home-logo" style={{ textAlign: 'center', marginBottom: 'var(--space-lg)' }}>管理后台</div>
        <div style={{ background: 'var(--color-surface)', borderRadius: 'var(--radius-md)', padding: 'var(--space-lg)', boxShadow: 'var(--shadow-card)' }}>
          <input
            type="password"
            placeholder="请输入管理员密钥"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            style={{
              width: '100%', padding: 'var(--space-md)', borderRadius: 'var(--radius-sm)',
              border: '2px solid var(--color-border)', fontSize: 16, marginBottom: 'var(--space-md)',
              fontFamily: 'var(--font-body)', outline: 'none',
            }}
          />
          <button className="btn btn-primary btn-lg" style={{ width: '100%' }} onClick={handleLogin}>
            进入管理后台
          </button>
        </div>
        <div style={{ textAlign: 'center', marginTop: 'var(--space-md)' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/')}>返回首页</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, padding: 'var(--space-lg) var(--space-md)' }}>
      {/* Header */}
      <div className="top-bar">
        <button className="btn-back" onClick={() => navigate('/')} aria-label="返回首页">←</button>
        <div className="title" style={{ flex: 1 }}>管理后台</div>
        <button className="btn btn-ghost btn-sm" onClick={() => { sessionStorage.removeItem(ADMIN_KEY); setAuthenticated(false); }}>
          退出
        </button>
      </div>

      {error && (
        <div style={{ background: '#FFF0F0', color: 'var(--color-wrong)', padding: 'var(--space-md)', borderRadius: 'var(--radius-sm)', marginBottom: 'var(--space-md)', fontSize: 14 }}>
          {error}
          <button style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setError('')}>×</button>
        </div>
      )}

      <div className="scroll-area">
        {/* Import Card */}
        <div style={{ background: 'var(--color-surface)', borderRadius: 'var(--radius-md)', padding: 'var(--space-lg)', boxShadow: 'var(--shadow-card)', marginBottom: 'var(--space-lg)' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', marginBottom: 'var(--space-md)' }}>导入题目</h3>

          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileRef.current?.click()}
            style={{
              border: `2px dashed ${dragOver ? 'var(--color-border-active)' : 'var(--color-border)'}`,
              borderRadius: 'var(--radius-md)', padding: 'var(--space-xl)',
              textAlign: 'center', cursor: 'pointer',
              background: dragOver ? '#F0F7FA' : 'transparent',
              transition: 'all var(--duration-fast) var(--ease-out)',
              marginBottom: 'var(--space-md)',
            }}
          >
            <input
              ref={fileRef}
              type="file"
              accept=".json"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
              }}
            />
            {file ? (
              <div>
                <div style={{ fontSize: 24, marginBottom: 4 }}>📄</div>
                <div style={{ fontWeight: 600 }}>{file.name}</div>
                {preview && <div style={{ color: 'var(--color-text-secondary)', fontSize: 14, marginTop: 4 }}>{preview.count} 道题目</div>}
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 32, marginBottom: 8 }}>📤</div>
                <div>拖拽 JSON 文件到此处，或点击选择</div>
              </div>
            )}
          </div>

          {preview && (
            <div style={{ marginBottom: 'var(--space-md)' }}>
              <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 4 }}>
                预览 (共 {preview.count} 道):
              </div>
              <div style={{ background: 'var(--color-bg)', padding: 'var(--space-md)', borderRadius: 'var(--radius-sm)', fontSize: 14, fontFamily: 'var(--font-body)' }}>
                {preview.first}
              </div>
            </div>
          )}

          <button
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={!preview || importing}
            onClick={handleImport}
          >
            {importing ? '导入中...' : `导入 ${preview?.count || 0} 道题目`}
          </button>

          {importResult && (
            <div style={{
              marginTop: 'var(--space-md)', padding: 'var(--space-md)', borderRadius: 'var(--radius-sm)',
              background: importResult.includes('失败') ? '#FFF0F0' : '#F0FFF4',
              color: importResult.includes('失败') ? 'var(--color-wrong)' : 'var(--color-correct)',
              fontSize: 14,
            }}>
              {importResult}
            </div>
          )}
        </div>

        {/* Question List */}
        <div style={{ background: 'var(--color-surface)', borderRadius: 'var(--radius-md)', padding: 'var(--space-lg)', boxShadow: 'var(--shadow-card)', marginBottom: 'var(--space-lg)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-md)' }}>
            <h3 style={{ fontFamily: 'var(--font-display)' }}>题库 ({questions.length} 道)</h3>
            <div>
              <button className="btn btn-primary btn-sm" style={{ marginRight: 8 }} onClick={openCreateForm}>+ 新增</button>
              <button className="btn btn-ghost btn-sm" onClick={fetchQuestions}>刷新</button>
              {questions.length > 0 && (
                confirmDeleteAll ? (
                  <span>
                    <button className="btn btn-secondary btn-sm" style={{ color: 'var(--color-wrong)', marginLeft: 8 }} onClick={handleDeleteAll}>
                      确认清空 {questions.length} 道
                    </button>
                    <button className="btn btn-ghost btn-sm" style={{ marginLeft: 4 }} onClick={() => setConfirmDeleteAll(false)}>
                      取消
                    </button>
                  </span>
                ) : (
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--color-wrong)', marginLeft: 8 }} onClick={() => setConfirmDeleteAll(true)}>
                    全部删除
                  </button>
                )
              )}
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-xl)', color: 'var(--color-text-secondary)' }}>加载中...</div>
          ) : questions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-xl)', color: 'var(--color-text-secondary)' }}>暂无题目，请导入 JSON</div>
          ) : (
            <div>
              {questions.map((q) => (
                <div
                  key={q.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: 'var(--space-sm) 0',
                    borderBottom: '1px solid var(--color-border)', fontSize: 14,
                  }}
                >
                  <span style={{ color: 'var(--color-text-secondary)', minWidth: 32, flexShrink: 0 }}>#{q.id}</span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{q.questionText}</span>
                  <span style={{
                    fontSize: 12, padding: '2px 8px', borderRadius: 'var(--radius-full)',
                    background: q.difficulty === 1 ? '#E8F5E9' : q.difficulty === 2 ? '#FFF8E1' : '#FFEBEE',
                    color: q.difficulty === 1 ? '#2E7D32' : q.difficulty === 2 ? '#F57F17' : '#C62828',
                    flexShrink: 0,
                  }}>
                    {DIFF_LABEL[q.difficulty]}
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', flexShrink: 0 }}>{q.tag}</span>
                  <button
                    onClick={() => openEditForm(q.id)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: '4px 6px',
                      color: 'var(--color-text-secondary)', borderRadius: 'var(--radius-sm)', flexShrink: 0,
                    }}
                    title="编辑"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDelete(q.id)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: '4px 6px',
                      color: 'var(--color-text-secondary)', borderRadius: 'var(--radius-sm)', flexShrink: 0,
                    }}
                    title="删除"
                  >
                    🗑
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create / Edit Modal */}
      {showForm && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 100,
            background: 'rgba(0,0,0,0.4)', display: 'flex',
            alignItems: 'flex-end', justifyContent: 'center',
          }}
          onClick={() => setShowForm(false)}
        >
          <div
            style={{
              background: 'var(--color-surface)', borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
              width: '100%', maxWidth: 420, maxHeight: '90dvh', overflowY: 'auto',
              padding: 'var(--space-lg)', animation: 'screenIn 0.25s var(--ease-out)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ fontFamily: 'var(--font-display)', marginBottom: 'var(--space-lg)' }}>
              {editingId ? '编辑题目' : '新增题目'}
            </h3>

            {formError && (
              <div style={{ background: '#FFF0F0', color: 'var(--color-wrong)', padding: 'var(--space-sm) var(--space-md)', borderRadius: 'var(--radius-sm)', marginBottom: 'var(--space-md)', fontSize: 14 }}>
                {formError}
              </div>
            )}

            {/* Question text */}
            <label style={labelStyle}>题目</label>
            <textarea
              value={formData.questionText}
              onChange={(e) => setFormData({ ...formData, questionText: e.target.value })}
              rows={3}
              style={inputStyle}
              placeholder="请输入题目内容"
            />

            {/* Options */}
            <label style={labelStyle}>选项</label>
            {formData.options.map((opt, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 14, minWidth: 20 }}>
                  {String.fromCharCode(65 + i)}.
                </span>
                <input
                  value={opt}
                  onChange={(e) => {
                    const next = [...formData.options] as [string, string, string, string];
                    next[i] = e.target.value;
                    setFormData({ ...formData, options: next });
                  }}
                  style={inputStyle}
                  placeholder={`选项 ${String.fromCharCode(65 + i)}`}
                />
              </div>
            ))}

            {/* Correct answer + Difficulty */}
            <div style={{ display: 'flex', gap: 'var(--space-md)' }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>正确答案</label>
                <select
                  value={formData.correctIndex}
                  onChange={(e) => setFormData({ ...formData, correctIndex: Number(e.target.value) })}
                  style={inputStyle}
                >
                  {['A', 'B', 'C', 'D'].map((l, i) => (
                    <option key={i} value={i}>{l}</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>难度</label>
                <select
                  value={formData.difficulty}
                  onChange={(e) => setFormData({ ...formData, difficulty: Number(e.target.value) })}
                  style={inputStyle}
                >
                  {DIFF_OPTIONS.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Tag */}
            <label style={labelStyle}>标签</label>
            <input
              value={formData.tag}
              onChange={(e) => setFormData({ ...formData, tag: e.target.value })}
              style={inputStyle}
              placeholder="如：喂养、健康、教育"
            />

            {/* Actions */}
            <div style={{ display: 'flex', gap: 'var(--space-md)', marginTop: 'var(--space-lg)' }}>
              <button
                className="btn btn-secondary"
                style={{ flex: 1 }}
                onClick={() => setShowForm(false)}
              >
                取消
              </button>
              <button
                className="btn btn-primary"
                style={{ flex: 1 }}
                disabled={saving}
                onClick={handleSave}
              >
                {saving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 13, fontWeight: 600,
  color: 'var(--color-text-secondary)', marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-sm)',
  border: '2px solid var(--color-border)', fontSize: 15,
  fontFamily: 'var(--font-body)', outline: 'none',
  marginBottom: 'var(--space-md)', background: 'var(--color-bg)',
};
